import {
  formatColor,
  parseColorValue,
  useRecentColorPickerSwatches
} from "@pathwright/ux/src/components/color/ColorPicker"
import defaults from "lodash/defaults"
import { DEFAULT_COLOR_PICKER_SWATCHES } from "../utils/colors"

function rgbaStringToHex(rgba) {
  const match = String(rgba).match(/(\d+)/g)
  if (!match || match.length < 3) return rgba
  const [r, g, b] = match.map(Number)
  return `#${[r, g, b]
    .map((n) => Math.round(n).toString(16).padStart(2, "0"))
    .join("")}`
}

export const DEFAULT_HEX_COLOR_PICKER_SWATCHES =
  DEFAULT_COLOR_PICKER_SWATCHES.map(rgbaStringToHex)

// Expects subset of tagsPropType
export const findMatchingTag = (tagName, tagsSubset) => {
  if (!tagName) return false

  const matchingTag = tagsSubset.find(
    (t) => t.name.toLowerCase() === tagName.toLowerCase()
  )
  return matchingTag
}

// Check if tag name already exists.
// Expects tags of tagsPropType.
export const getTagNameAlreadyExists = (tagName, tags) =>
  Boolean(findMatchingTag(tagName, tags.all))

// Check if tag name has already been filtered.
// Expects tags of tagsPropType.
export const getTagNameAlreadyFiltered = (tagName, tags) =>
  Boolean(findMatchingTag(tagName, tags.filtered))

// Check if tag name has already been selected.
// Expects tags of tagsPropType.
export const getTagNameAlreadySelected = (tagName, tags) =>
  Boolean(findMatchingTag(tagName, tags.selected))

/**
 * mergeSelectedTags
 * @param {tagsPropType} tags
 * @param {[tagPropType]} selectedTags
 * @returns {tagsPropType} with updated selected and filtered keys.
 */
export const mergeSelectedTags = (tags, selectedTags) => {
  // force array
  selectedTags = [].concat(selectedTags)
  // find if a tag exists in the selectedTags that does not
  // yet exist in tags.all
  const newTags = [
    ...new Set(
      selectedTags.filter(
        (selectedTag) => !tags.all.find((tag) => selectedTag.name === tag.name)
      )
    )
  ]

  // new tag/s was/were created
  if (newTags.length) {
    return {
      all: [...tags.all, ...newTags],
      filtered: [...tags.filtered, ...newTags],
      selected: selectedTags
    }
  }

  // an existing tag was selected
  return {
    all: tags.all,
    filtered: tags.filtered,
    selected: selectedTags
  }
}

/**
 * Removes an existing tag from a given list of tags.
 * @param {[tagPropType]} tagsList
 * @returns {[tagPropType]}
 */
export const removeTag = (tagsList, removedTag) =>
  tagsList.reduce(
    (tags, tag) => (tag.name === removedTag.name ? tags : [...tags, tag]),
    []
  )

export { useRecentColorPickerSwatches }

export const useMostRecentColorPickerSwatches = () =>
  useRecentColorPickerSwatches(undefined, DEFAULT_HEX_COLOR_PICKER_SWATCHES)

export const useDefaultTag = (tagOptions = {}, tags) => {
  const [colorPickerSwatches] = useMostRecentColorPickerSwatches()

  const getTag = (tagOptions) => {
    const originalColor = colorPickerSwatches.list[colorPickerSwatches.index]
    // Tag must be formatted as hex, so we need to parse the original color.
    const parsedColor = formatColor(parseColorValue(originalColor), "hex")
    // Supply default color based on the most recently used ColorPicker swatch.
    const tag = defaults(tagOptions, {
      color: parsedColor
    })
    return tag
  }

  // Providing tags ensures we're using an existing tag, if found.
  if (tags) {
    const matchingTag = findMatchingTag(tagOptions.name, tags.all)
    if (matchingTag) return getTag(matchingTag)
  }

  return getTag(tagOptions)
}
